// Client contracts and queries of the maturity module (BLD-26, docs/maturity/05).
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { ApiError, get } from "./api.ts";
import type { Definition } from "../../shared/maturity/definition.ts";
import type { Responses } from "../../shared/maturity/responses.ts";
import type { DimensionScore } from "../../shared/maturity/scoring.ts";
import type { CompareTree } from "../../shared/maturity/compare.ts";
import type { Answer } from "../../shared/maturity/responses.ts";
import type { CategoryState, CategoryStateInfo } from "../../shared/maturity/workflow.ts";

export type ScenarioKind = "diagnosis" | "prognosis";
export const SCENARIO_LABEL: Record<ScenarioKind, string> = { diagnosis: "Diagnóstico", prognosis: "Prognóstico" };
export const SCENARIO_TAGLINE: Record<ScenarioKind, string> = { diagnosis: "Como estamos", prognosis: "Como poderemos ficar" };
export const PROGNOSIS_NOTE = "Prognóstico: projeção considerando a implementação bem-sucedida do plano.";
export const CONFLICT_MESSAGE = "Esta avaliação foi alterada por outro instrutor. Confira a versão atual antes de salvar.";

export interface MaturityInstitution { id: string; name: string; linkable_plans: { id: string; name: string }[] }
export interface VersionSummary { id: string; revision_number: number; state: "draft" | "published"; published_at: string | null; question_count: number; assessment_count: number; version: number }
export interface TemplateSummary { id: string; name: string; source_key: string | null; retired_at: string | null; version: number; versions: VersionSummary[] }
export interface VersionDetail extends VersionSummary { template_id: string; template_name: string; retired_at: string | null; definition: Definition; method_code: string; published_by_name: string | null; updated_at: string }

export type AssessmentStatus = "diagnosis_draft" | "diagnosis_finalized" | "prognosis_draft" | "complete";
export const STATUS_TEXT: Record<AssessmentStatus, string> = {
  diagnosis_draft: "Diagnóstico em preenchimento",
  diagnosis_finalized: "Diagnóstico encerrado",
  prognosis_draft: "Prognóstico em preenchimento",
  complete: "Diagnóstico e prognóstico encerrados",
};

export interface ScenarioBrief { id: string; state: "draft" | "finalized"; version: number; answered: number; total: number; finalized_at: string | null; finalized_by_name: string | null }
export interface AssessmentListItem {
  id: string; name: string; reference_date: string; responsible_label: string; linked_plan_name: string | null; template_name: string; revision_number: number;
  status: AssessmentStatus; updated_at: string;
  diagnosis: { state: string; answered: number; total: number };
  prognosis: { state: string; answered: number; total: number } | null;
}
export interface AssessmentDetail {
  id: string; institution: { id: string; name: string }; name: string; reference_date: string; responsible_label: string;
  linked_plan: { id: string; name: string } | null; based_on: { id: string; name: string } | null; version: number; created_at: string;
  template: { id: string; name: string; version_id: string; revision_number: number; method_code: string };
  definition: Definition;
  scenarios: { diagnosis: ScenarioBrief | null; prognosis: ScenarioBrief | null };
  capabilities: { can_edit_identity: boolean; can_start_prognosis: boolean };
}
export interface ScenarioDetail {
  id: string; kind: ScenarioKind; state: "draft" | "finalized"; version: number; responses: Responses; answered: number; total: number;
  scores: DimensionScore; updated_at: string; updated_by_name: string; finalized_at: string | null; finalized_by_name: string | null;
  /** BLD-28: review state per category (missing = open, version 0). */
  categories: Record<string, CategoryStateInfo>;
}

// ---- BLD-28 category workflow ----------------------------------------------------------------------------
export interface WorkflowAssignment {
  category_id: string; group_id: string; group_name: string; membership_id: string; member_name: string;
  manager_membership_id: string | null; manager_name: string | null; version: number;
}
export interface WorkflowGroup {
  id: string; name: string; manager_membership_id: string | null; manager_name: string | null;
  members: { membership_id: string; name: string; role: string }[];
}
export interface WorkflowData {
  plan: { id: string; name: string } | null;
  can_assign: boolean;
  groups: WorkflowGroup[];
  assignments: Record<string, WorkflowAssignment>;
}
export function useWorkflow(institutionId: string, id: string) {
  return useQuery({ queryKey: mKey(institutionId, "assessment", id, "workflow"), queryFn: async () => (await get<{ data: WorkflowData }>(`${mBase(institutionId)}/assessments/${id}/workflow`)).data });
}

/** Participant view inside the workshop (/api/plans/:planId/diagnostico). */
export interface WorkshopCategoryCaps { can_edit: boolean; can_submit: boolean; can_validate: boolean; can_return: boolean }
export interface WorkshopCategoryItem {
  id: string; code: string; title: string; subdimension_title: string; group_name: string; answerer_name: string;
  roles: { answerer: boolean; manager: boolean }; state: CategoryState; note: string | null; answered: number; total: number;
  capabilities: WorkshopCategoryCaps;
}
export interface WorkshopAssessmentItem {
  id: string; name: string; reference_date: string;
  scenarios: { kind: ScenarioKind; state: "draft" | "finalized"; categories: WorkshopCategoryItem[] }[];
}
export interface WorkshopCategoryDetail extends CategoryStateInfo {
  assessment: { id: string; name: string; reference_date: string };
  kind: ScenarioKind;
  scenario_state: "draft" | "finalized";
  subdimension: { code: string; title: string };
  category: { id: string; code: string; title: string; questions: { id: string; code: string; text: string }[] };
  group_name: string; answerer_name: string;
  roles: { answerer: boolean; manager: boolean };
  entries: Record<string, { answer: Answer | null; group_comment: string | null; evaluator_unit: string | null }>;
  reference: Record<string, Answer | null> | null;
  capabilities: WorkshopCategoryCaps;
}
export const workshopKey = (planId: string, ...rest: unknown[]) => ["plan", planId, "diagnostico", ...rest] as const;

/** [] when the module is off (404) or nothing is assigned to the caller. */
export function useWorkshopDiagnosis(planId: string) {
  return useQuery({
    queryKey: workshopKey(planId),
    queryFn: async () => {
      try {
        return (await get<{ data: WorkshopAssessmentItem[] }>(`/plans/${planId}/diagnostico`)).data;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}
export interface DashboardData {
  assessment_id: string;
  diagnosis: { state: string; version: number; finalized_at: string | null } | null;
  prognosis: { state: string; version: number; finalized_at: string | null } | null;
  tree: CompareTree;
}

export const mKey = (...rest: unknown[]) => ["maturity", ...rest] as const;
export const mBase = (institutionId: string) => `/maturity/institutions/${institutionId}`;

/** Institutions where the caller can use the module; [] when the feature is off (404) or for participants. */
export function useMaturityInstitutions() {
  return useQuery({
    queryKey: mKey("institutions"),
    queryFn: async () => {
      try {
        return (await get<{ data: MaturityInstitution[] }>("/maturity/institutions")).data;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/** The institution id travels in the query string, so every module page is a deep link. */
export function useInstitutionParam(): string | null {
  return new URLSearchParams(useSearch()).get("instituicao");
}
export const withInstitution = (path: string, institutionId: string) => `${path}${path.includes("?") ? "&" : "?"}instituicao=${institutionId}`;

export function useAssessment(institutionId: string, id: string) {
  return useQuery({ queryKey: mKey(institutionId, "assessment", id), queryFn: async () => (await get<{ data: AssessmentDetail }>(`${mBase(institutionId)}/assessments/${id}`)).data });
}
